import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBackToList } from '../../hooks/useBackToList';
import axios from '../../Api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSearch, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import AlertModal from './components/AlertModal';
import './CreateQuotaProject.css';

const CreateQuotaProject = () => {
  const navigate = useNavigate();
  // Retour vers la liste en conservant recherche/filtre/page (ils vivent dans l'URL).
  const backToList = useBackToList('/quotas');
  const [surveys, setSurveys] = useState([]);
  const [loadingSurveys, setLoadingSurveys] = useState(true);
  const [loading, setLoading] = useState(false);
  const [alertModal, setAlertModal] = useState({ show: false, title: '', message: '', type: 'info' });
  const [surveySearch, setSurveySearch] = useState('');
  const [showSurveyDropdown, setShowSurveyDropdown] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [selectedWave, setSelectedWave] = useState(null);
  const surveyDropdownRef = useRef(null);

  const [formData, setFormData] = useState({
    survey_id: '',
    survey_wave_id: '',
    overall_target: '',
    fieldwork_start_date: '',
    fieldwork_end_date: '',
    notes: ''
  });

  useEffect(() => {
    fetchSurveys();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (surveyDropdownRef.current && !surveyDropdownRef.current.contains(e.target)) {
        setShowSurveyDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSurveys = async () => {
    try {
      setLoadingSurveys(true);
      const response = await axios.get('/quotas/projects/available-surveys');
      setSurveys(response.data.data || []);
    } catch (error) {
      console.error('Error fetching surveys:', error);
      setAlertModal({
        show: true,
        title: 'Erreur',
        message: 'Erreur lors du chargement des enquêtes',
        type: 'error'
      });
    } finally {
      setLoadingSurveys(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectSurvey = (survey) => {
    setSelectedSurvey(survey);
    setSelectedWave(null);
    setSurveySearch('');
    setShowSurveyDropdown(false);
    if (survey.is_tracker) {
      // For tracker surveys the vague drives the objective and dates;
      // wait for a wave to be picked before filling them.
      setFormData(prev => ({
        ...prev,
        survey_id: String(survey.id),
        survey_wave_id: '',
        overall_target: '',
        fieldwork_start_date: '',
        fieldwork_end_date: '',
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        survey_id: String(survey.id),
        survey_wave_id: '',
        overall_target: survey.general_target ? String(survey.general_target) : prev.overall_target,
        fieldwork_start_date: survey.date_debut || prev.fieldwork_start_date,
        fieldwork_end_date: survey.date_fin || prev.fieldwork_end_date,
      }));
    }
  };

  const handleSelectWave = (wave) => {
    if (wave.has_quota) return; // already has a quota
    setSelectedWave(wave);
    setFormData(prev => ({
      ...prev,
      survey_wave_id: String(wave.id),
      overall_target: wave.target != null ? String(wave.target) : '',
      fieldwork_start_date: wave.date_debut || '',
      fieldwork_end_date: wave.date_fin || '',
    }));
  };

  const filteredSurveys = surveys.filter(s => {
    if (!surveySearch) return true;
    const term = surveySearch.toLowerCase();
    return (s.title || '').toLowerCase().includes(term) || String(s.limesurvey_id).includes(term);
  });

  // Determine whether the selected survey can still receive a new quota:
  // - non-tracker: only one quota allowed
  // - tracker: at least one vague must be free (and vagues must exist)
  const getBlockInfo = () => {
    if (!selectedSurvey) return { blocked: false, message: '' };
    if (selectedSurvey.is_tracker) {
      const waves = selectedSurvey.waves || [];
      if (waves.length === 0) {
        return { blocked: true, message: "Cette enquête tracker n'a aucune vague définie. Ajoutez des vagues à l'enquête avant de créer un quota." };
      }
      if (waves.every(w => w.has_quota)) {
        return { blocked: true, message: "Toutes les vagues de cette enquête ont déjà un quota. Impossible d'en créer un nouveau." };
      }
      return { blocked: false, message: '' };
    }
    if (selectedSurvey.has_quota) {
      return { blocked: true, message: "Cette enquête a déjà un quota. Une enquête non-tracker ne peut avoir qu'un seul quota." };
    }
    return { blocked: false, message: '' };
  };
  const blockInfo = getBlockInfo();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (getBlockInfo().blocked) {
      return;
    }

    if (!formData.survey_id || !formData.overall_target) {
      setAlertModal({
        show: true,
        title: 'Champs requis',
        message: 'Veuillez remplir tous les champs obligatoires',
        type: 'warning'
      });
      return;
    }

    if (selectedSurvey?.is_tracker && !formData.survey_wave_id) {
      setAlertModal({
        show: true,
        title: 'Vague requise',
        message: 'Veuillez sélectionner une vague pour cette enquête tracker',
        type: 'warning'
      });
      return;
    }

    try {
      setLoading(true);
      const payload = {
        survey_id: parseInt(formData.survey_id),
        survey_wave_id: formData.survey_wave_id ? parseInt(formData.survey_wave_id) : null,
        overall_target: parseInt(formData.overall_target),
        fieldwork_start_date: formData.fieldwork_start_date || null,
        fieldwork_end_date: formData.fieldwork_end_date || null,
        notes: formData.notes || null
      };

      const response = await axios.post('/quotas/projects', payload);
      const projectId = response.data.data.id;
      
      // Redirect to builder
      navigate(`/quotas/${projectId}`);
    } catch (error) {
      console.error('Error creating project:', error);
      setAlertModal({
        show: true,
        title: 'Erreur',
        message: error.response?.data?.message || 'Erreur lors de la création du projet',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-quota-project">
      <div className="page-header">
        <h1>Nouveau Quota</h1>
      </div>

      <div className="create-form-container">
        <form onSubmit={handleSubmit} className="create-form">
          {blockInfo.blocked && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '12px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14, fontWeight: 500 }}>
              {blockInfo.message}
            </div>
          )}
          <div className="form-section">
            <h2>Informations générales</h2>
            
            <div className="form-group">
              <label>
                Enquête <span className="required-indicator">*</span>
              </label>
              {loadingSurveys ? (
                <div>Chargement des enquêtes...</div>
              ) : (
                <div ref={surveyDropdownRef} style={{ position: 'relative' }}>
                  {selectedSurvey ? (
                    <div
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db',
                        background: '#f0fdf4', cursor: 'pointer', fontSize: 14
                      }}
                      onClick={() => { setSelectedSurvey(null); setSelectedWave(null); setFormData(prev => ({ ...prev, survey_id: '', survey_wave_id: '' })); setSurveySearch(''); setShowSurveyDropdown(true); }}
                    >
                      <span style={{ fontWeight: 600, color: '#15803d' }}>
                        {selectedSurvey.title} <span style={{ fontWeight: 400, color: '#6b7280' }}>({selectedSurvey.limesurvey_id})</span>
                      </span>
                      <FontAwesomeIcon icon={faTimes} style={{ color: '#94a3b8', fontSize: 13 }} />
                    </div>
                  ) : (
                    <div style={{ position: 'relative' }}>
                      <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 13 }} />
                      <input
                        type="text"
                        value={surveySearch}
                        onChange={e => { setSurveySearch(e.target.value); setShowSurveyDropdown(true); }}
                        onFocus={() => setShowSurveyDropdown(true)}
                        placeholder="Rechercher une enquête..."
                        style={{ width: '100%', padding: '10px 36px 10px 36px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                      />
                      <FontAwesomeIcon icon={faChevronDown} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 12 }} />
                    </div>
                  )}
                  {showSurveyDropdown && !selectedSurvey && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                      background: '#fff', border: '1px solid #d1d5db', borderRadius: 8,
                      marginTop: 4, maxHeight: 220, overflowY: 'auto',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}>
                      {filteredSurveys.length === 0 ? (
                        <div style={{ padding: '12px 16px', color: '#94a3b8', fontSize: 13 }}>Aucune enquête trouvée</div>
                      ) : (
                        filteredSurveys.map(s => (
                          <div
                            key={s.id}
                            onClick={() => handleSelectSurvey(s)}
                            style={{
                              padding: '10px 16px', cursor: 'pointer', fontSize: 14,
                              borderBottom: '1px solid #f1f5f9',
                              transition: 'background 0.15s'
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <div style={{ fontWeight: 600, color: '#1e293b' }}>{s.title}</div>
                            <div style={{ fontSize: 12, color: '#94a3b8' }}>ID: {s.limesurvey_id}</div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Vague selector — tracker surveys only */}
            {selectedSurvey?.is_tracker && (
              <div className="form-group">
                <label>
                  Vague <span className="required-indicator">*</span>
                </label>
                {(selectedSurvey.waves || []).length === 0 ? (
                  <small className="form-hint">Aucune vague définie pour cette enquête.</small>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {selectedSurvey.waves.map((wave) => {
                      const isSelected = String(formData.survey_wave_id) === String(wave.id);
                      const disabled = wave.has_quota;
                      return (
                        <div
                          key={wave.id}
                          onClick={() => handleSelectWave(wave)}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '10px 12px', borderRadius: 8,
                            border: `1px solid ${isSelected ? '#2563eb' : '#d1d5db'}`,
                            background: disabled ? '#f3f4f6' : (isSelected ? '#eff6ff' : '#fff'),
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled ? 0.6 : 1,
                            fontSize: 14
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 600, color: '#1e293b' }}>{wave.label || `Vague`}</div>
                            <div style={{ fontSize: 12, color: '#94a3b8' }}>
                              {wave.date_debut || '?'} → {wave.date_fin || '?'} · {wave.target != null ? `${wave.target} complétions` : '—'}
                            </div>
                          </div>
                          {disabled && (
                            <span style={{ fontSize: 12, color: '#ef4444', fontWeight: 600 }}>Quota existant</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                <small className="form-hint">Chaque vague a son propre quota. L'objectif est défini par la vague.</small>
              </div>
            )}

            <div className="form-group">
              <label>
                Objectif global * <span className="required-indicator">*</span>
              </label>
              <input
                type="number"
                name="overall_target"
                value={formData.overall_target}
                onChange={handleInputChange}
                placeholder="ex: 1000"
                min="1"
                required
                readOnly={selectedSurvey?.is_tracker || false}
                disabled={blockInfo.blocked}
              />
              <small className="form-hint">
                {selectedSurvey?.is_tracker
                  ? 'Défini automatiquement par la vague sélectionnée'
                  : 'Nombre total de complétions nécessaires'}
              </small>
            </div>
          </div>

          <div className="form-section">
            <h2>Paramètres optionnels</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label>Date de début terrain</label>
                <input
                  type="date"
                  name="fieldwork_start_date"
                  value={formData.fieldwork_start_date}
                  onChange={handleInputChange}
                  disabled={blockInfo.blocked}
                />
              </div>

              <div className="form-group">
                <label>Date de fin terrain</label>
                <input
                  type="date"
                  name="fieldwork_end_date"
                  value={formData.fieldwork_end_date}
                  onChange={handleInputChange}
                  min={formData.fieldwork_start_date}
                  disabled={blockInfo.blocked}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows="4"
                placeholder="Notes optionnelles sur le projet..."
                disabled={blockInfo.blocked}
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate('/quotas')}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading || blockInfo.blocked}
            >
              {loading ? 'Création...' : 'Créer le quota'}
            </button>
          </div>
        </form>
      </div>

      <AlertModal
        show={alertModal.show}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ show: false, title: '', message: '', type: 'info' })}
      />
    </div>
  );
};

export default CreateQuotaProject;

